/**
 * ministryRhythmAnalyzer.js
 * Detects ministry rhythm patterns and imbalances.
 *
 * Analyzes:
 *   - Entity signal history (are visits happening regularly?)
 *   - Workflow type distribution (too much admin? too little pastoral?)
 *   - Session rhythm (busy vs. sparse weeks)
 *
 * Language: discernimiento ministerial — never analytics.
 */

import { getAllEntityHistories }  from "../kevia-relationships/relationalMemoryEngine";
import { getActiveWorkflows }     from "../kevia-workflows/workflowState";
import { WORKFLOW_TYPES }         from "../kevia-workflows/workflowSchemas";
import { detectSessionRhythm }    from "../kevia-runtime/humanRhythm";

const PASTORAL_TYPES = new Set([
  WORKFLOW_TYPES.FAMILY_FOLLOWUP,
  WORKFLOW_TYPES.VISIT_CYCLE,
  WORKFLOW_TYPES.PRAYER_FOLLOWUP,
  WORKFLOW_TYPES.INACTIVE_MEMBER_RECOVERY,
]);

const GROWTH_TYPES = new Set([
  WORKFLOW_TYPES.BAPTISM_PREPARATION,
]);

/**
 * @typedef {"pastoral_care"|"evangelism"|"administrative"|"mixed"|"sparse"} DominantActivity
 */

/**
 * Analyze the ministry rhythm from available state.
 *
 * @returns {{
 *   dominantActivity: DominantActivity,
 *   imbalance:        string | null,
 *   suggestion:       string | null,
 *   entityCount:      number,
 *   highRiskCount:    number,
 * }}
 */
export function analyzeMinistryRhythm() {
  const histories   = getAllEntityHistories();
  const activeWfs   = getActiveWorkflows();
  const rhythm      = detectSessionRhythm();

  // Count entities by risk level from relationship memory
  const risky       = ["followup_urgent", "sensitive"];
  const highRisk    = histories.filter((h) => risky.includes(h.signals[0]?.tone)).length;

  // Workflow distribution
  const pastoralWfs = activeWfs.filter((w) => PASTORAL_TYPES.has(w.type)).length;
  const growthWfs   = activeWfs.filter((w) => GROWTH_TYPES.has(w.type)).length;
  const totalWfs    = activeWfs.length;

  // Determine dominant activity
  let dominantActivity = "mixed";

  if (totalWfs === 0 && histories.length === 0) {
    dominantActivity = "sparse";
  } else if (pastoralWfs > 0 && growthWfs === 0) {
    dominantActivity = "pastoral_care";
  } else if (growthWfs > 0 && pastoralWfs === 0) {
    dominantActivity = "evangelism";
  } else if (totalWfs === 0 && histories.length > 0) {
    dominantActivity = "administrative";
  }

  // Detect imbalances
  let imbalance = null;
  let suggestion = null;

  if (highRisk >= 3 && pastoralWfs === 0) {
    imbalance  = `Hay ${highRisk} personas o familias en riesgo sin procesos activos de seguimiento.`;
    suggestion = "Convendría abrir algunos procesos de seguimiento pastoral.";
  } else if (pastoralWfs >= 5 && growthWfs === 0) {
    imbalance  = "Mucho seguimiento activo, pero poco énfasis en crecimiento espiritual.";
    suggestion = "Podría ser buen momento para avanzar en discipulado con quienes están listos.";
  } else if (rhythm.fatigueScore >= 0.65 && pastoralWfs >= 3) {
    imbalance  = "Sesión intensa con varios procesos activos simultáneamente.";
    suggestion = "Tal vez convenga enfocarse en 1-2 prioridades en lugar de todo a la vez.";
  }

  return {
    dominantActivity,
    imbalance,
    suggestion,
    entityCount:  histories.length,
    highRiskCount: highRisk,
  };
}
